import { LegalLayout } from "../components/site/LegalLayout";

const sections = [
  {
    id: "quick-actions",
    number: "01",
    title: "Quick actions you can take right now",
    content: (
      <>
        <p>You have control over the personal information we collect about you. Inside the App, you can:</p>
        <ul>
          <li><strong>Edit your profile</strong> — Profile tab → Edit profile → update or remove name, bio, specialty, photo, license info.</li>
          <li><strong>Disable push notifications</strong> — iOS Settings → Notifications → ProBeauty Connect → toggle off. We immediately stop sending pushes to that device.</li>
          <li><strong>Turn off location</strong> — iOS Settings → Privacy & Security → Location Services → ProBeauty Connect → Never. The map “Use my location” button will no longer work, but everything else continues.</li>
          <li><strong>Delete a listing</strong> — Manage tab → swipe a listing → Delete. Removes it instantly from the marketplace.</li>
          <li><strong>Delete a message thread</strong> — Open a conversation → tap the chat header → Delete conversation.</li>
          <li><strong>Manage subscriptions (Verified Pro)</strong> — iOS Settings → tap your name → Subscriptions → ProBeauty Connect → Cancel.</li>
        </ul>
      </>
    ),
  },
  {
    id: "requests",
    number: "02",
    title: "Requests we honor",
    content: (
      <>
        <p><strong>Access / data export.</strong> Request a machine-readable export (JSON) of all the personal information we hold about you. We send it to your registered email within 30 days at no cost.</p>
        <p><strong>Correction.</strong> Most fields are editable in-app (Edit profile). For fields you can't self-edit (e.g., license decisions, transaction history), email us with the correction request.</p>
        <p><strong>Deletion (right to be forgotten).</strong> See our <a href="/delete-account">Delete Your Account</a> page for the easiest way to permanently remove your account. We process deletion within 30 days. Data we're legally required to retain (e.g., financial records for tax) is anonymized rather than retained linked to identity.</p>
        <p><strong>Restriction of processing.</strong> Ask us to pause processing your data while you contest something. Useful if you think we have inaccurate data and we're still using it.</p>
        <p><strong>Objection.</strong> Object to specific uses of your data. We will stop those uses unless we have a compelling legal basis to continue.</p>
        <p><strong>Opt out of sale / sharing.</strong> We do not sell or share your personal information for advertising or any commercial purpose. There is nothing to opt out of, but the right is preserved under CCPA/CPRA. If we ever start doing so, we will give you 30 days notice and an opt-out mechanism.</p>
        <p><strong>Withdraw consent.</strong> Where we rely on consent (e.g., camera/photo/location permissions), you can withdraw it anytime in iOS Settings. Some app features will stop working accordingly.</p>
      </>
    ),
  },
  {
    id: "submit",
    number: "03",
    title: "How to submit a request",
    content: (
      <ul>
        <li>Email <a href="mailto:privacy@probeauty.app">privacy@probeauty.app</a> from the email address on your ProBeauty Connect account.</li>
        <li>Include in the subject: the type of request (e.g., “Data export request”, “Account deletion request”).</li>
        <li>In the body: your full name on the account + a brief description.</li>
        <li>We acknowledge within 5 business days and resolve within 30 days. For complex requests we may extend by 60 days and notify you.</li>
      </ul>
    ),
  },
  {
    id: "verification",
    number: "04",
    title: "Verification",
    content: (
      <p>To prevent fraud, we may verify your identity before fulfilling sensitive requests (e.g., data export, deletion). We do this by asking you to confirm via the email associated with your account, or by signing in to the app and pressing a “confirm” button we send via push.</p>
    ),
  },
  {
    id: "agents",
    number: "05",
    title: "Authorized agents",
    content: (
      <p>You may use an authorized agent to submit requests on your behalf. The agent must provide written authorization signed by you. We may still need to verify your identity directly.</p>
    ),
  },
  {
    id: "appeals",
    number: "06",
    title: "Appeals",
    content: (
      <p>If we deny a request, you may appeal by emailing <a href="mailto:privacy@probeauty.app">privacy@probeauty.app</a> with subject “Privacy appeal”. We respond to appeals within 30 days.</p>
    ),
  },
  {
    id: "contact",
    number: "07",
    title: "Contact",
    content: (
      <>
        <p><strong>Privacy Team</strong></p>
        <p>Email: <a href="mailto:privacy@probeauty.app">privacy@probeauty.app</a></p>
        <p>For California residents: this page satisfies our CCPA/CPRA “Your Privacy Choices” disclosure.</p>
      </>
    ),
  },
];

export default function PrivacyChoices() {
  return (
    <LegalLayout
      eyebrow="ProBeauty Connect"
      titleLines={["Your Privacy", "Choices"]}
      meta="Effective Date: May 29, 2026"
      intro="You have control over the personal information we collect about you. This page explains your options and how to exercise them."
      sections={sections}
    />
  );
}
